// src/Dashboard/pages/user/News/components/NewsCard.jsx

import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createDOMPurify from "dompurify";
import {
  FaEdit,
  FaEye,
  FaShareAlt,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { toAbsolutePhotoUrl } from "../../../../../services/newsPhotoServices";

import {
  FALLBACK_NEWS_IMAGE,
  formatNewsDate,
  getAuthorName,
  getNewsDate,
  onImgError,
} from "../utils/newsDashboardUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "news-dashboard-card-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-6 text-slate-600",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-[#013E8E]",
  "hover:prose-a:underline",
  "dark:prose-a:text-blue-300",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-[#013E8E]",
  "prose-blockquote:bg-blue-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-blue-500/10",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Les médias sont masqués dans l’aperçu de la carte
  "[&_img]:hidden",
  "[&_iframe]:hidden",
  "[&_video]:hidden",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

const sanitizeHtml = (html = "") => {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
};

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function NewsCard({
  news,
  canModify,
  onRead,
  onEdit,
  onDelete,
}) {
  const [isExcerptTruncated, setIsExcerptTruncated] =
    useState(false);

  const excerptRef = useRef(null);

  const coverSrc = useMemo(() => {
    return (
      toAbsolutePhotoUrl(news?.image_url || "") ||
      FALLBACK_NEWS_IMAGE
    );
  }, [news?.image_url]);

  /*
   * Le contenu HTML complet est conservé.
   * La limitation est uniquement visuelle.
   */
  const excerptHtml = useMemo(() => {
    const html = news?.content || "";
    const plainContent = stripHtml(html);

    if (!plainContent) {
      return sanitizeHtml(
        '<p style="font-style: italic;">Contenu vide.</p>',
      );
    }

    return sanitizeHtml(html);
  }, [news?.content]);

  /*
   * Détermine si l’extrait dépasse sa hauteur maximale.
   */
  useLayoutEffect(() => {
    const element = excerptRef.current;

    if (!element) {
      setIsExcerptTruncated(false);
      return undefined;
    }

    let animationFrameId;

    const checkOverflow = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const hasOverflow =
          element.scrollHeight > element.clientHeight + 2;

        setIsExcerptTruncated(hasOverflow);
      });
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;

    resizeObserver?.observe(element);
    window.addEventListener("resize", checkOverflow);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [excerptHtml, news?.id]);

  const createdAt = getNewsDate(news);
  const authorName = getAuthorName(news);

  const share = async () => {
    try {
      const url =
        `${window.location.origin}` +
        `${window.location.pathname}` +
        `#news-${news.id}`;

      await navigator.clipboard.writeText(url);

      toast.success("Lien copié.");
    } catch (error) {
      console.error("Copie du lien impossible :", error);
      toast.error("Impossible de copier le lien.");
    }
  };

  return (
    <article
      id={`news-${news.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950"
    >
      {/* Image de couverture */}

      <button
        type="button"
        onClick={onRead}
        className="relative block w-full overflow-hidden bg-slate-100 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-[#013E8E]/40 dark:bg-slate-900"
        aria-label={`Lire l’actualité ${
          news?.title || "Sans titre"
        }`}
      >
        <div
          className="w-full"
          style={{ aspectRatio: "16 / 9" }}
        >
          <img
            src={coverSrc}
            alt={news?.title || "Illustration actualité"}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
            onError={onImgError}
          />
        </div>

        {!news?.is_active ? (
          <span className="absolute left-3 top-3 inline-flex rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
            Inactif
          </span>
        ) : null}
      </button>

      {/* Contenu de la carte */}

      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-1 flex-col">
          <h3 className="line-clamp-2 text-base font-bold leading-tight text-slate-900 dark:text-white sm:text-lg">
            {news?.title || "Sans titre"}
          </h3>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Publié le{" "}
            <span className="font-bold text-slate-700 dark:text-slate-200">
              {formatNewsDate(createdAt)}
            </span>

            {authorName ? (
              <>
                {" "}
                par{" "}
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {authorName}
                </span>
              </>
            ) : null}
          </p>

          {/* Aperçu HTML enrichi */}

          <div className="relative mt-3 min-h-[144px]">
            <div
              ref={excerptRef}
              className={cn(
                RICH_TEXT_CLASSES,
                "max-h-[165px] overflow-hidden pr-1",
              )}
              style={{ tabSize: 4 }}
              dangerouslySetInnerHTML={{
                __html: excerptHtml,
              }}
            />

            {isExcerptTruncated ? (
              <div
                className={cn(
                  "pointer-events-none absolute inset-x-0 bottom-0 h-20",
                  "bg-gradient-to-t from-white via-white/95 to-transparent",
                  "dark:from-slate-950 dark:via-slate-950/95",
                )}
                aria-hidden="true"
              />
            ) : null}
          </div>
        </div>

        {/* Actions */}

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRead}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-800"
            >
              <FaEye />
              Lire
            </button>

            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
            >
              <FaShareAlt />
              Partager
            </button>
          </div>

          {canModify ? (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onEdit}
                title="Modifier"
                aria-label="Modifier"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaEdit />
              </button>

              <button
                type="button"
                onClick={onDelete}
                title="Supprimer"
                aria-label="Supprimer"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
              >
                <FaTrash />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}